/**
 * CopyItemsFromDocumentModal Component
 * Başka bir belgeden (fatura/teklif/sipariş/irsaliye) kalemleri mevcut forma aktarır.
 * - Server-side pagination + filtreleme
 * - Side toggle (Verilen/Alınan)
 * - "Ekle" (append) veya "Değiştir" (replace) stratejisi
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Radio,
  Paper,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../../../config/api';
import { accountingApi } from '../../../../../config/apiClient';
import type { InvoiceItem } from '../types/invoice.types';
import { formatQuantityForDisplay } from '../../utils/invoiceCalculations';
import { readInvoiceLot } from '../../utils/invoiceLot';

type ModeType = 'invoice' | 'offer' | 'order' | 'waybill';
type SideType = 'sales' | 'purchase';
type ImportStrategy = 'append' | 'replace';

interface CopyItemsFromDocumentModalProps {
  open: boolean;
  onClose: () => void;
  mode: ModeType;
  initialSide: SideType;
  currentDocumentId?: string;
  onItemsImport: (items: InvoiceItem[], strategy: ImportStrategy) => void;
}

interface DocumentListItem {
  id: string;
  documentNumber: string;
  date: string;
  accountName: string;
  currency: string;
  grandTotal: number;
  status?: string;
}

// Yeni satır id üretici (mevcut formatla uyumlu)
const newRowId = () => `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// Mode → label eşlemesi (TR)
const TR_LABEL: Record<ModeType, { titleNoun: string; numberCol: string; dateCol: string }> = {
  invoice: { titleNoun: 'Fatura', numberCol: 'Fatura No', dateCol: 'Fatura Tarihi' },
  offer: { titleNoun: 'Teklif', numberCol: 'Teklif No', dateCol: 'Teklif Tarihi' },
  order: { titleNoun: 'Sipariş', numberCol: 'Sipariş No', dateCol: 'Sipariş Tarihi' },
  waybill: { titleNoun: 'İrsaliye', numberCol: 'İrsaliye No', dateCol: 'Tarih' },
};

const EN_LABEL: Record<ModeType, { titleNoun: string; numberCol: string; dateCol: string }> = {
  invoice: { titleNoun: 'Invoice', numberCol: 'Invoice No', dateCol: 'Invoice Date' },
  offer: { titleNoun: 'Offer', numberCol: 'Offer No', dateCol: 'Offer Date' },
  order: { titleNoun: 'Order', numberCol: 'Order No', dateCol: 'Order Date' },
  waybill: { titleNoun: 'Waybill', numberCol: 'Waybill No', dateCol: 'Date' },
};

// Mode + side → liste isteği
function buildListRequest(mode: ModeType, side: SideType, params: {
  page: number;
  pageSize: number;
  searchTerm?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  status?: string;
}) {
  const { page, pageSize, searchTerm, sortColumn, sortDirection, status } = params;

  if (mode === 'invoice') {
    return ACCOUNTING_API.INVOICE_BY_COMPANY({
      page,
      pageSize,
      search: searchTerm || undefined,
      invoiceType: side === 'sales' ? 'SALES' : 'PURCHASE',
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection || undefined,
    });
  }
  if (mode === 'offer') {
    return ACCOUNTING_API.OFFER_BY_COMPANY_PAGED({
      page,
      pageSize,
      searchTerm: searchTerm || undefined,
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection || undefined,
      offerType: side === 'sales' ? 'SALES_OFFER' : 'PURCHASE_OFFER',
      status: status || undefined,
    });
  }
  if (mode === 'order') {
    return ACCOUNTING_API.ORDER_BY_COMPANY_PAGED({
      page,
      pageSize,
      searchTerm: searchTerm || undefined,
      sortColumn: sortColumn || undefined,
      sortDirection: sortDirection || undefined,
      orderType: side === 'sales' ? 'SALES_ORDER' : 'PURCHASE_ORDER',
      status: status || undefined,
    });
  }
  // waybill — sadece sales (alış irsaliye yok)
  return ACCOUNTING_API.WAYBILL_BY_COMPANY_PAGED({
    page,
    pageSize,
    searchTerm: searchTerm || undefined,
    sortColumn: sortColumn || undefined,
    sortDirection: sortDirection || undefined,
  });
}

// Mode → detay endpoint
function buildDetailUrl(mode: ModeType, id: string) {
  switch (mode) {
    case 'invoice':
      return ACCOUNTING_API.INVOICE_BY_ID(id);
    case 'offer':
      return ACCOUNTING_API.OFFER_BY_ID(id);
    case 'order':
      return ACCOUNTING_API.ORDER_BY_ID(id);
    case 'waybill':
      return ACCOUNTING_API.WAYBILL_BY_ID(id);
  }
}

// Liste response → DocumentListItem
function mapDocumentResponse(mode: ModeType, raw: any): DocumentListItem {
  if (mode === 'invoice') {
    return {
      id: String(raw.id),
      documentNumber: raw.invoiceNumber || '',
      date: raw.invoiceDate ? String(raw.invoiceDate).split('T')[0] : '',
      accountName: raw.account?.companyName || raw.accountName || '',
      currency: raw.currency || 'TRY',
      grandTotal: Number(raw.grandTotal) || 0,
      status: raw.status || undefined,
    };
  }
  if (mode === 'offer') {
    return {
      id: String(raw.id),
      documentNumber: raw.offerNumber || '',
      date: raw.offerDate ? String(raw.offerDate).split('T')[0] : '',
      accountName: raw.accountName || '',
      currency: raw.currency || 'TRY',
      grandTotal: Number(raw.grandTotal) || 0,
      status: raw.status || undefined,
    };
  }
  if (mode === 'order') {
    return {
      id: String(raw.id),
      documentNumber: raw.orderNumber || '',
      date: raw.orderDate ? String(raw.orderDate).split('T')[0] : '',
      accountName: raw.accountName || '',
      currency: raw.currency || 'TRY',
      grandTotal: Number(raw.grandTotal) || 0,
      status: raw.status || undefined,
    };
  }
  // waybill
  return {
    id: String(raw.id),
    documentNumber: raw.waybillNumber || '',
    date: raw.waybillDate ? String(raw.waybillDate).split('T')[0] : '',
    accountName: raw.accountName || '',
    currency: raw.currency || 'TRY',
    grandTotal: Number(raw.grandTotal) || 0,
  };
}

// Detail response item → InvoiceItem (yeni id ile, eski belgeyle bağı yok)
function mapDocumentItemsToInvoiceItems(mode: ModeType, items: any[]): InvoiceItem[] {
  if (!Array.isArray(items)) return [];

  return items.map((item: any) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const vatRate = Number(item.vatRate) || 0;
    const enteredPrice =
      mode === 'waybill'
        ? unitPrice
        : (item.enteredPrice !== undefined && item.enteredPrice !== null
          ? Number(item.enteredPrice)
          : unitPrice);
    const priceType: 'VAT_EXCLUDED' | 'VAT_INCLUDED' =
      mode === 'waybill' ? 'VAT_EXCLUDED' : (item.priceType || 'VAT_EXCLUDED');

    // Backend itemId/stockId: invoice/offer/order'da itemId, waybill'de stockId
    const sourceItemId = mode === 'waybill' ? item.stockId : item.itemId;
    const isManual =
      item.isManualItem === true ||
      sourceItemId === null ||
      sourceItemId === undefined ||
      sourceItemId === '';

    const id = newRowId();
    const itemId = isManual ? `manual_${id}` : String(sourceItemId);

    const netTotal = item.netTotal !== undefined ? Number(item.netTotal) : 0;
    const grossTotal = item.grossTotal !== undefined ? Number(item.grossTotal) : 0;
    const lineTotal = item.lineTotal !== undefined ? Number(item.lineTotal) : netTotal;
    const vatAmount = item.vatAmount !== undefined ? Number(item.vatAmount) : (grossTotal - netTotal);

    return {
      id,
      itemId,
      itemName: item.itemName || '',
      quantity,
      displayQuantity: formatQuantityForDisplay(quantity),
      unit: item.unit || 'adet',
      enteredPrice,
      priceType,
      unitPrice,
      unitPriceWithVat: unitPrice * (1 + vatRate / 100),
      vatRate,
      sctRate: Number(item.sctRate) || 0,
      discountRate: Number(item.discountRate) || 0,
      discountAmount: Number(item.discountAmount) || 0,
      lineTotal,
      netTotal,
      vatAmount,
      sctAmount: Number(item.sctAmount) || 0,
      grossTotal,
      warehouseId: item.warehouseId ? String(item.warehouseId) : undefined,
      ...readInvoiceLot(item),
    } as InvoiceItem;
  });
}

export const CopyItemsFromDocumentModal: React.FC<CopyItemsFromDocumentModalProps> = ({
  open,
  onClose,
  mode,
  initialSide,
  currentDocumentId,
  onItemsImport,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const lbl = isTr ? TR_LABEL[mode] : EN_LABEL[mode];

  // Side: waybill modunda hep 'sales'
  const [side, setSide] = useState<SideType>(mode === 'waybill' ? 'sales' : initialSide);

  // Liste durumu
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [docs, setDocs] = useState<DocumentListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  // Modal her açıldığında state'i sıfırla
  useEffect(() => {
    if (open) {
      setSide(mode === 'waybill' ? 'sales' : initialSide);
      setPage(0);
      setRowsPerPage(10);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setStatusFilter('');
      setSelectedDocId(null);
      setError(null);
    }
  }, [open, initialSide, mode]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Side / filtre / arama değişince sayfa 0'a dön ve seçimi temizle
  useEffect(() => {
    setPage(0);
    setSelectedDocId(null);
  }, [side, debouncedSearchTerm, statusFilter]);

  // Liste fetch
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchList = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = buildListRequest(mode, side, {
          page: page + 1,
          pageSize: rowsPerPage,
          searchTerm: debouncedSearchTerm || undefined,
          sortColumn: mode === 'invoice' ? 'invoiceDate' : mode === 'offer' ? 'offerDate' : mode === 'order' ? 'orderDate' : 'waybillDate',
          sortDirection: 'desc',
          status: statusFilter || undefined,
        });

        const response = await accountingApi.get(url);
        const result = response.data;
        if (cancelled) return;

        if (result?.success === false) {
          setDocs([]);
          setTotalCount(0);
          setError(result?.message || tt('Liste alınamadı', 'Failed to load list'));
          return;
        }

        const rawItems = Array.isArray(result?.data) ? result.data : [];
        const mapped = rawItems.map((r: any) => mapDocumentResponse(mode, r));
        // Edit modunda kendi belgesini gizle (sadece kendi tarafına bakıyorsa)
        const filtered =
          currentDocumentId && side === initialSide
            ? mapped.filter((d: DocumentListItem) => String(d.id) !== String(currentDocumentId))
            : mapped;

        setDocs(filtered);
        setTotalCount(Number(result?.totalCount) || filtered.length);
      } catch (err: any) {
        if (cancelled) return;
        console.error('[CopyItemsFromDocumentModal] Liste hatası:', err);
        setDocs([]);
        setTotalCount(0);
        setError(err?.message || tt('Liste alınamadı', 'Failed to load list'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchList();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, side, page, rowsPerPage, debouncedSearchTerm, statusFilter, currentDocumentId, initialSide]);

  const formatCurrency = (val: number, currency: string) =>
    new Intl.NumberFormat(isTr ? 'tr-TR' : 'en-US', {
      style: 'currency',
      currency: currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

  const handleConfirm = async (strategy: ImportStrategy) => {
    if (!selectedDocId) return;

    if (strategy === 'replace') {
      const ok = window.confirm(
        tt(
          'Mevcut tüm kalemler silinip seçilen belgenin kalemleri ile değiştirilecek. Devam edilsin mi?',
          'All current items will be removed and replaced with items from the selected document. Continue?'
        )
      );
      if (!ok) return;
    }

    try {
      setImporting(true);
      setError(null);
      const url = buildDetailUrl(mode, selectedDocId);
      const response = await accountingApi.get(url);
      const result = response.data;

      if (!result?.success) {
        throw new Error(result?.message || tt('Belge detayı alınamadı', 'Failed to fetch document details'));
      }

      const data = result.data || {};
      const rawItems = data.items || data.Items || [];
      const mappedItems = mapDocumentItemsToInvoiceItems(mode, rawItems);

      if (mappedItems.length === 0) {
        setError(tt('Seçilen belgede kalem bulunmuyor.', 'The selected document has no items.'));
        return;
      }

      onItemsImport(mappedItems, strategy);
      onClose();
    } catch (err: any) {
      console.error('[CopyItemsFromDocumentModal] Detay/aktarım hatası:', err);
      setError(err?.message || tt('Aktarım sırasında hata oluştu', 'An error occurred during import'));
    } finally {
      setImporting(false);
    }
  };

  // Status select seçenekleri (offer/order için)
  const statusOptions = useMemo(() => {
    if (mode === 'offer') {
      return [
        { value: '', label: tt('Tümü', 'All') },
        { value: 'pending', label: tt('Beklemede', 'Pending') },
        { value: 'accepted', label: tt('Kabul Edildi', 'Accepted') },
        { value: 'rejected', label: tt('Reddedildi', 'Rejected') },
        { value: 'expired', label: tt('Süresi Geçti', 'Expired') },
      ];
    }
    if (mode === 'order') {
      return [
        { value: '', label: tt('Tümü', 'All') },
        { value: 'pending', label: tt('Beklemede', 'Pending') },
        { value: 'confirmed', label: tt('Onaylandı', 'Confirmed') },
        { value: 'shipped', label: tt('Sevk Edildi', 'Shipped') },
        { value: 'delivered', label: tt('Teslim Edildi', 'Delivered') },
        { value: 'cancelled', label: tt('İptal Edildi', 'Cancelled') },
      ];
    }
    return [];
  }, [mode, isTr]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="div">
          {tt(`Başka ${lbl.titleNoun}'den Kalem Aktar`, `Copy Items From Another ${lbl.titleNoun}`)}
        </Typography>
        <IconButton onClick={onClose} disabled={importing} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Side toggle (waybill modunda gizli) */}
        {mode !== 'waybill' && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={side}
              exclusive
              onChange={(_, val) => {
                if (val) setSide(val);
              }}
              size="small"
              color="primary"
            >
              <ToggleButton value="sales">
                {tt('Verilen (Satış)', 'Outgoing (Sales)')}
              </ToggleButton>
              <ToggleButton value="purchase">
                {tt('Alınan (Alış)', 'Incoming (Purchase)')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Üst bar: arama + status filter */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={tt('Ara (belge no, cari adı...)', 'Search (number, account...)')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {(mode === 'offer' || mode === 'order') && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{tt('Durum', 'Status')}</InputLabel>
              <Select
                value={statusFilter}
                label={tt('Durum', 'Status')}
                onChange={(e) => setStatusFilter(e.target.value as string)}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tablo */}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 'bold' }}>{lbl.numberCol}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{lbl.dateCol}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{tt('Cari', 'Account')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {tt('Tutar (KDV Dahil)', 'Total (Incl. VAT)')}
                </TableCell>
                {mode !== 'waybill' && (
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {tt('Durum', 'Status')}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={mode === 'waybill' ? 5 : 6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={mode === 'waybill' ? 5 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {tt('Kayıt bulunamadı.', 'No records found.')}
                  </TableCell>
                </TableRow>
              ) : (
                docs.map((doc) => (
                  <TableRow
                    key={doc.id}
                    hover
                    selected={selectedDocId === doc.id}
                    onClick={() => setSelectedDocId(doc.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Radio checked={selectedDocId === doc.id} size="small" />
                    </TableCell>
                    <TableCell>{doc.documentNumber || '-'}</TableCell>
                    <TableCell>{doc.date || '-'}</TableCell>
                    <TableCell>{doc.accountName || '-'}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(doc.grandTotal, doc.currency)}
                    </TableCell>
                    {mode !== 'waybill' && (
                      <TableCell align="center">
                        {doc.status ? <Chip size="small" label={doc.status} /> : '-'}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={tt('Sayfa boyutu', 'Page size')}
          labelDisplayedRows={({ from, to, count }) =>
            tt(`${from}-${to} / ${count}`, `${from}-${to} of ${count}`)
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={importing}>
          {tt('İptal', 'Cancel')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => handleConfirm('replace')}
          variant="outlined"
          color="warning"
          disabled={!selectedDocId || importing || loading}
        >
          {importing ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {tt('Mevcut Kalemleri Değiştir', 'Replace Current Items')}
        </Button>
        <Button
          onClick={() => handleConfirm('append')}
          variant="contained"
          color="primary"
          disabled={!selectedDocId || importing || loading}
          sx={{ ml: 1 }}
        >
          {importing ? <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> : null}
          {tt('Kalemlere Ekle', 'Append to Items')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
